import { Leaf, Search, Filter, LayoutDashboard, Locate } from 'lucide-react';
import { PlusCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { categories } from '@/constants/categories';

/**
 * Map Header Component
 * Contains logo, action buttons, search bar, and category filter
 */
const MapHeader = ({
  showFacebookBanner,
  searchQuery,
  setSearchQuery,
  handleSearchSubmit,
  selectedCategory,
  setSelectedCategory,
  filteredBoxes,
  boxes,
  user,
  navigate,
  handleFindNearby,
  locatingUser
}) => {
  return (
    <header className={`absolute left-0 right-0 z-[1000] bg-white/95 backdrop-blur-md shadow-lg ${showFacebookBanner ? 'top-10' : 'top-0'}`}>
      <div className="container mx-auto px-4 py-4">
        {/* Top Row: Logo and Action Buttons */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="bg-gradient-to-br from-green-400 to-green-600 p-3 rounded-2xl shadow-lg">
              <Leaf className="w-8 h-8 text-white" data-testid="logo-icon" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800" data-testid="site-title">Neighbours Pantry</h1>
              <p className="text-sm text-gray-600">Your Local Source For Something Fresh</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 w-full md:w-auto justify-center md:justify-end">
            <Button
              onClick={handleFindNearby}
              disabled={locatingUser}
              variant="outline"
              className="border-purple-500 text-purple-700 hover:bg-purple-50"
              data-testid="find-nearby-btn"
            >
              <Locate className="w-4 h-4 mr-2" />
              {locatingUser ? 'Locating...' : 'Near Me'}
            </Button>
            <Button
              onClick={() => navigate('/submit-box')}
              variant="outline"
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              data-testid="submit-box-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Box
            </Button>
            {user ? (
              <Button
                onClick={() => navigate(user.role === 'admin' ? '/admin/dashboard' : '/vendor/dashboard')}
                variant="default"
                className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                data-testid="dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/vendor/login')}
                variant="outline"
                className="border-green-500 text-green-700 hover:bg-green-50"
                data-testid="vendor-signup-btn"
              >
                Vendor Login
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filter Row */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search suburb/box... (Press Enter to zoom)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchSubmit}
              className="pl-10 py-6 rounded-full border-2 border-green-200 focus:border-green-400"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Filter className="text-gray-500 w-5 h-5 hidden md:block" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full md:w-[200px] rounded-full border-2 border-green-200" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value} data-testid={`category-${cat.value}`}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Count */}
        <div className="mt-3 text-sm text-gray-600" data-testid="results-count">
          Showing {filteredBoxes.length} of {boxes.length} honesty boxes
        </div>
      </div>
    </header>
  );
};

export default MapHeader;
